import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  Checkbox,
  Tooltip as AntTooltip,
  Row,
  Col,
  Typography,
  Card,
  Space,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Option } = Select;
const { Title, Text } = Typography;

const Time = ({ streetName, data }) => {
  const options = ["1min", "5min", "10min", "15min", "30min"];
  const defaultSelectedOption = "10min";
  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
  const [extractedGraphData, setExtractedGraphData] = useState([]);
  const [vehicleVisibility, setVehicleVisibility] = useState({});
  const [mediaTtSec, setMediaTtSec] = useState(null);
  const [ticks, setTicks] = useState({});

  const colors = useMemo(
    () => [
      "#353A0A",
      "#A1E340",
      "#ffc658",
      "#ff7300",
      "#ff84f0",
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#af111c",
      "#c02e17",
    ],
    []
  );

  // Helper to normalize IDs (handles "455.0" vs 455 format)
  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    const parsed = typeof id === 'string' ? parseFloat(id) : id;
    return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
  };

  useEffect(() => {
    if (streetName) {
      const normalizedStreetName = normalizeId(streetName);
      const streetData = data.find((street) => normalizeId(street.id) === normalizedStreetName);

      if (streetData?.indicatoriGlobali) {
        setMediaTtSec(streetData.indicatoriGlobali.mediaTtSec);
      }

      if (
        streetData?.serieTemporale?.ttime &&
        streetData.serieTemporale.inputCount[selectedOption]
      ) {
        const graphData = streetData.serieTemporale.ttime[selectedOption];
        const newData = Object.keys(graphData).map((vehicleType, index) => ({
          name: vehicleType,
          data: Object.entries(graphData[vehicleType]).map(([key, value]) => ({
            x: parseInt(key),
            y: value,
          })),
          color: colors[index % colors.length],
        }));
        setExtractedGraphData(newData);

        const initialVisibility = newData.reduce((acc, v) => {
          acc[v.name] = true;
          return acc;
        }, {});
        setVehicleVisibility(initialVisibility);
      }

      setTicks({
        "1min": [10, 20, 30, 40, 50, 60],
        "5min": [10, 20, 30, 40, 50, 60],
        "10min": [10, 20, 30, 40, 50, 60],
        "15min": [15, 30, 45, 60],
        "30min": [30, 40, 50, 60],
      });
    }
  }, [streetName, selectedOption, data, colors]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setVehicleVisibility((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Title with tooltip */}
      <Space align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, color: "#353A0A" }}>
          Tempo
        </Title>
        <AntTooltip title="Tempo impiegato da un veicolo, entrato in uno specifico intervallo di tempo, per percorrere la sezione stradale selezionata. La durata degli intervalli può essere selezionata attraverso l'apposito menu a tendina.">
          <InfoCircleOutlined style={{ fontSize: 16, color: "#768079" }} />
        </AntTooltip>
      </Space>

      <Row gutter={[16, 16]}>
        {/* Line Chart */}
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  allowDataOverflow={true}
                  ticks={ticks[selectedOption]}
                  label={{
                    value: "Time",
                    position: "insideBottom",
                    offset: -10,
                    fill: "#353A0A",
                  }}
                />
                <YAxis
                  label={{
                    value: "sec",
                    angle: -90,
                    position: "insideLeft",
                    fill: "#353A0A",
                  }}
                />
                <Tooltip />
                {extractedGraphData.map((vehicle) =>
                  vehicleVisibility[vehicle.name] ? (
                    <Line
                      key={vehicle.name}
                      type="monotone"
                      dataKey="y"
                      data={vehicle.data}
                      name={vehicle.name}
                      stroke={vehicle.color}
                    />
                  ) : null
                )}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Checkbox Controls */}
        <Col xs={24} lg={8}>
          <Card
            bordered={false}
            style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
          >
            <Space direction="vertical" size={10}>
              {extractedGraphData.map((vehicle) => (
                <Checkbox
                  key={vehicle.name}
                  checked={vehicleVisibility[vehicle.name]}
                  onChange={handleCheckboxChange}
                  name={vehicle.name}
                  style={{ color: vehicle.color }}
                >
                  {vehicle.name}
                </Checkbox>
              ))}
              <Text strong style={{ color: "#353A0A" }}>{`Tempo medio: ${
                mediaTtSec || "-"
              }`}</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Time interval select */}
      <div style={{ marginTop: 20, display: "flex", justifyContent: "center" }}>
        <Select
          value={selectedOption}
          onChange={setSelectedOption}
          style={{ width: 140 }}
          bordered
        >
          {options.map((opt) => (
            <Option key={opt} value={opt}>
              {opt}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default Time;
